import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, RotateCw, Trash2, XCircle, CheckCircle2, Clock, AlertTriangle, PlayCircle } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const STATUS_LABEL = {
  pending: { icon: Clock, color: "#f59e0b", text: "Pending retry" },
  failed_permanent: { icon: XCircle, color: "#ef4444", text: "Failed (3 attempts)" },
  sent: { icon: CheckCircle2, color: "#10b981", text: "Resolved" },
  resolved: { icon: CheckCircle2, color: "#10b981", text: "Resolved" },
};

function relativeAge(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Legacy retry-queue view — preserved verbatim from the pre-Batch-3 page.
 * Stays available under the "Retry queue (legacy)" tab so existing workflows
 * keep working.
 */
export function RetryQueueLegacy() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, failed_permanent: 0, sent: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [retryingAll, setRetryingAll] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email/queue${filter ? `?status=${filter}` : ""}`, { headers: auth() });
      setRows(res.data?.rows || []);
      setCounts(res.data?.counts || { pending: 0, failed_permanent: 0, sent: 0 });
    } catch {
      toast.error("Failed to load email queue");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleRetryAll = async () => {
    if (!window.confirm("Retry every pending email now?")) return;
    setRetryingAll(true);
    try {
      const res = await axios.post(`${API}/email/queue/retry-all`, {}, { headers: auth() });
      toast.success(`Retried ${res.data.attempted}: ${res.data.succeeded} ✅ / ${res.data.failed} ❌`);
      await fetchQueue();
    } catch {
      toast.error("Retry-all failed");
    } finally {
      setRetryingAll(false);
    }
  };

  const handleRetryOne = async (row) => {
    setBusyId(row.id);
    try {
      const res = await axios.post(`${API}/email/queue/${row.id}/retry`, {}, { headers: auth() });
      if (res.data.ok) toast.success("Email re-sent"); else toast.error("Retry failed");
      await fetchQueue();
    } catch {
      toast.error("Retry failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (row) => {
    if (!window.confirm("Dismiss this row from the queue?")) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/email/queue/${row.id}`, { headers: auth() });
      toast.success("Dismissed");
      await fetchQueue();
    } catch {
      toast.error("Dismiss failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="retry-queue-legacy">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] text-[#676664]/60 italic">Failed emails are auto-retried every 5 min</div>
        <div className="flex items-center gap-2">
          <button onClick={fetchQueue} className="p-2 border border-[#282828]/12 hover:border-[#9A7B4F]/40 bg-white" data-testid="queue-refresh-btn">
            <RefreshCw className={`w-4 h-4 text-[#676664] ${loading ? "animate-spin" : ""}`} />
          </button>
          <Button onClick={handleRetryAll} disabled={retryingAll || counts.pending === 0}
            className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-5" data-testid="retry-all-btn">
            {retryingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlayCircle className="w-4 h-4 mr-2" /> Retry All ({counts.pending})</>}
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#282828]/8">
        {[
          { key: "pending", label: `Pending (${counts.pending})` },
          { key: "failed_permanent", label: `Failed (${counts.failed_permanent})` },
          { key: "sent", label: `Resolved (${counts.sent})` },
          { key: "", label: "All" },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-5 py-2.5 text-[11px] tracking-wider transition-colors border-b-2 -mb-[1px] ${filter === f.key ? "border-[#9A7B4F] text-[#9A7B4F] font-medium" : "border-transparent text-[#676664]/50 hover:text-[#676664]"}`}
            data-testid={`queue-filter-${f.key || "all"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Rows */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 bg-white border border-[#282828]/8" data-testid="queue-empty">
          <CheckCircle2 className="w-8 h-8 text-emerald-400/40 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[13px] text-[#676664]/40">
            {filter === "pending" ? "No emails pending retry — you're all caught up" :
             filter === "failed_permanent" ? "No permanently failed emails" :
             filter === "sent" ? "No resolved retries yet" : "Queue is empty"}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#282828]/8 divide-y divide-[#282828]/5">
          {rows.map(row => {
            const cfg = STATUS_LABEL[row.status] || { icon: AlertTriangle, color: "#9ca3af", text: row.status };
            const Icon = cfg.icon;
            const busy = busyId === row.id;
            const isResolvable = row.status === "pending" || row.status === "failed_permanent";
            return (
              <div key={row.id} className="px-4 py-3 flex items-start gap-3" data-testid={`queue-row-${row.id}`}>
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: cfg.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] text-[#282828] font-medium font-mono">{row.email_type}</span>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.text}</span>
                    <span className="text-[10px] text-[#676664]/40">· attempt {row.attempts}/3</span>
                  </div>
                  <div className="text-[11px] text-[#676664]/70 mt-0.5 truncate">
                    to <strong>{row.to_email}</strong> · last try {relativeAge(row.last_attempt)}{row.next_attempt && row.status === "pending" ? ` · next ${relativeAge(row.next_attempt)}` : ""}
                  </div>
                  {row.error_message && (
                    <div className="text-[10px] text-red-600/80 mt-1 break-words" title={row.error_message}>
                      {row.error_message}
                    </div>
                  )}
                  <div className="text-[10px] text-[#676664]/40 mt-0.5">entity: {row.entity_kind || "—"} / {(row.entity_id || row.booking_id || "").slice(0, 24)}</div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isResolvable && (
                    <button onClick={() => handleRetryOne(row)} disabled={busy}
                      className="px-2.5 py-1.5 text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5 tracking-wider uppercase inline-flex items-center gap-1"
                      data-testid={`retry-row-${row.id}`}>
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}Retry
                    </button>
                  )}
                  <button onClick={() => handleDismiss(row)} disabled={busy}
                    className="p-1.5 text-[#676664]/40 hover:text-red-500" title="Dismiss" data-testid={`dismiss-row-${row.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
